import { SdkContext } from '@/lib/sdk-official/internal'
import { getOfficialClient } from '@/lib/sdk-official/loader'
import type { PlayerEpisode, PlayerProgressBody, PlayerProgressResult, PlayerQueueItem } from './types'

/**
 * F-178: the player's calls. A context of its own over the shared official
 * client (the same token, community and language as `sdk`), so the player
 * does not depend on the modules' resource for episodes.
 */
const ctx = new SdkContext()

const queueOf = (result: { items?: PlayerQueueItem[] } | PlayerQueueItem[] | null | undefined): PlayerQueueItem[] =>
  Array.isArray(result) ? result : result?.items || []

export const playerApi = {
  getEpisode(id: string): Promise<PlayerEpisode> {
    return ctx.call({ httpMethod: 'GET', path: '/media-episodes/{id}' }, { id })
  },

  saveProgress(id: string, body: PlayerProgressBody): Promise<PlayerProgressResult> {
    return ctx.call({ httpMethod: 'PUT', path: '/media-episodes/{id}/progress', hasBody: true }, { id, body })
  },

  async getQueue(): Promise<PlayerQueueItem[]> {
    return queueOf(await ctx.call({ httpMethod: 'GET', path: '/me/media-queue' }))
  },

  async setQueue(episodeIds: string[]): Promise<PlayerQueueItem[]> {
    return queueOf(await ctx.call({ httpMethod: 'PUT', path: '/me/media-queue', hasBody: true }, { body: { episodeIds } }))
  },

  async addToQueue(episodeId: string): Promise<PlayerQueueItem[]> {
    return queueOf(await ctx.call({ httpMethod: 'POST', path: '/me/media-queue', hasBody: true }, { body: { episodeId } }))
  },

  async removeFromQueue(episodeId: string): Promise<PlayerQueueItem[]> {
    return queueOf(await ctx.call({ httpMethod: 'DELETE', path: '/me/media-queue/{episodeId}' }, { episodeId }))
  }
}

/**
 * The last progress of a page that is going away (reload, tab closed, another
 * domain). The SDK's fetch is cancelled with the page; `keepalive` is not.
 */
export const saveProgressOnUnload = (id: string, body: PlayerProgressBody): void => {
  const client = getOfficialClient()
  const config = client?.config as { baseUrl?: string; token?: string; communityId?: string; memberId?: string; language?: string } | undefined
  if (!config?.baseUrl || !config.token || !config.communityId) return
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${config.token}`,
    'X-CommunityId': config.communityId
  }
  if (config.memberId) headers['X-MemberId'] = config.memberId
  if (config.language) headers['Accept-Language'] = config.language
  try {
    void fetch(`${config.baseUrl.replace(/\/$/, '')}/api/media-episodes/${encodeURIComponent(id)}/progress`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body),
      keepalive: true
    }).catch(() => undefined)
  } catch {
    /* Nothing to do on the way out. */
  }
}
